import { type FC, memo, type ReactNode } from "react";
import { AddFeature, RecordParticipationTypes } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { MobileBannerContent } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/MobileBannerContent";
import { MobileCredibilityBadge } from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/MobileCredibilityBadge";
import {
  MobileCredibilityBadgePlacement,
  MobileCredibilityBadgePlacementContext,
} from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/MobileCredibilityBadgePlacement";
import { useMobileCredibilityBadgePlacementExperiment } from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/useMobileCredibilityBadgePlacementExperiment";
import {
  HeaderAndBannerContainer,
  mobileCredibilityBadgeBelowHero,
  mobileCredibilityBadgeBelowHeroLabel,
} from "src/website-apps/responsive/stubhub-performer/components/new-mobile-layout/styles";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { canShowWorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/eligibility";
import { mobilePlacement } from "src/website-apps/shared/components/world-cup-fan-protect/styles";
import { WorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/WorldCupFanProtectDisplay";
import { PerformerViewMode } from "src/website-apps/shared/constants/performer-view-mode";
import { usePerformerViewMode } from "src/website-apps/shared/hooks/usePerformerViewMode";
interface Props {
  header: ReactNode;
}

/**
 * Container for header and banner for the new mobile layout shown on mobile and (actual) tablet.
 * header passed in as prop but banner rendered within this component
 *
 * note: replaces glassImageBanner
 */
export const MobileLayoutHeaderAndBanner: FC<Props> = memo(({ header }) => {
  const appContext = useAppContext();
  const model = usePerformerPageModelData();
  const performerViewMode = usePerformerViewMode();
  const suppressBanner = performerViewMode === PerformerViewMode.Residency;
  const credibilityBadgePlacement = useMobileCredibilityBadgePlacementExperiment(!suppressBanner);
  const canShowWorldCupFanProtect = canShowWorldCupFanProtectDisplay(
    model.categorySummary.categoryId,
    appContext.cobrandId,
  );

  return (
    <MobileCredibilityBadgePlacementContext.Provider value={credibilityBadgePlacement}>
      <div className={HeaderAndBannerContainer}>
        {header}
        {!suppressBanner && <MobileBannerContent />}
        <MobileCredibilityBadge
          placement={MobileCredibilityBadgePlacement.BELOW_HERO}
          className={mobileCredibilityBadgeBelowHeroLabel}
          wrapperClassName={mobileCredibilityBadgeBelowHero}
        />
        <AddFeature
          id={FeatureSetEnum.TMWorldCupFanProtectDisplay}
          recordType={RecordParticipationTypes.IN_VIEW}
          canParticipate={() => canShowWorldCupFanProtect}
          feature={() => (
            <div className={mobilePlacement}>
              <WorldCupFanProtectDisplay mode="mobile" />
            </div>
          )}
        />
      </div>
    </MobileCredibilityBadgePlacementContext.Provider>
  );
});

MobileLayoutHeaderAndBanner.displayName = "MobileLayoutHeaderAndBanner";
